import { encodePeriodValue } from '@components'

import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useGlobalFiscalYear } from '../../../../../../contexts/globalFiscalYearContext'
import { useLocalStorageUncontrolled } from '../../../../../../hooks/useLocalStorageUncontrolled'
import { useQueryParams } from '../../../../../../hooks/useQueryParams'
import { getPeriodValueFromFiscalYear } from '../../../../../../utils/getPeriodValueFromFiscalYear'
import { ReportsRoute } from '../../../../enums/reportsRoute'
import { ReportOptions } from '../../../../types/reportOptions'
import { getLocalStorageFiltersValidated } from '../../../../utils/getLocalStorageFiltersValidated'
import { getReportsFiltersStorageKey } from '../../../../utils/getReportsFiltersStorageKey'

interface TrialBalanceReportOptionsUrl {
  hideZeroes?: string
  period?: string
}

interface TrialBalanceFiltersContextState {
  appliedFilters: ReportOptions
  setQueryParams: (
    filters:
      | Partial<TrialBalanceReportOptionsUrl>
      | ((prevParams: Partial<TrialBalanceReportOptionsUrl>) => Partial<TrialBalanceReportOptionsUrl>),
  ) => void
}

const TrialBalanceFiltersContext = createContext<TrialBalanceFiltersContextState | undefined>(undefined)

const defaultReportsQuery: TrialBalanceReportOptionsUrl = {
  hideZeroes: 'true',
  period: undefined,
}

interface TrialBalanceFiltersContextProviderProps {
  children: ReactNode
}

export const TrialBalanceFiltersContextProvider = ({ children }: TrialBalanceFiltersContextProviderProps) => {
  const { organization } = useUserOrganization()
  const trialBalanceStorageKey = getReportsFiltersStorageKey(ReportsRoute.TrialBalance, organization?.id)

  const { setItem, getItem } = useLocalStorageUncontrolled(trialBalanceStorageKey)

  const { fiscalYear: globalFiscalYear } = useGlobalFiscalYear()
  const defaultPeriodValue =
    globalFiscalYear && organization?.id
      ? encodePeriodValue(getPeriodValueFromFiscalYear(globalFiscalYear), organization.id)
      : undefined

  const localStorageFilters = getLocalStorageFiltersValidated(defaultReportsQuery, getItem())

  const [queryParams, setQueryParams] = useQueryParams<TrialBalanceReportOptionsUrl>({
    defaultQuery: {
      ...defaultReportsQuery,
      ...(defaultPeriodValue ? { period: defaultPeriodValue } : {}),
      ...localStorageFilters,
    },
  })

  useEffect(() => {
    const localStorageFiltersString = JSON.stringify(localStorageFilters)
    const queryParamsString = JSON.stringify(queryParams)

    if (organization?.id && localStorageFiltersString !== queryParamsString) {
      setItem(queryParamsString)
    }
  }, [localStorageFilters, organization?.id, queryParams, setItem])

  const appliedFilters = useMemo(() => {
    const { hideZeroes } = queryParams

    return {
      ...queryParams,
      hideZeroes: hideZeroes === 'true',
    }
  }, [queryParams])

  return (
    <TrialBalanceFiltersContext.Provider
      value={{
        appliedFilters,
        setQueryParams,
      }}
    >
      {children}
    </TrialBalanceFiltersContext.Provider>
  )
}

export const useTrialBalanceFilters = () => {
  const context = useContext(TrialBalanceFiltersContext)

  if (!context) {
    throw new Error('TrialBalanceFiltersContext is missing in the module!')
  }

  return context
}
